import { type ProcedimentoConTask } from '@framework/api-client/api/backoffice/schemas/procedimentoConTask.ts';
import { type GetFrontRichiesteParams } from '@framework/api-client/api/inquiries-compile/schemas/getFrontRichiesteParams.ts';
import { TitleDivider } from '@framework/engine/schema/widgets/repository/graphics/divider/style/TitleDivider.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { Flex } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NetworkAlertPage } from '../../../errors/network-alert/NetworkAlertPage.tsx';
import { StartSecondaryInquiryButton } from '../../components/StartSecondaryInquiryButton.tsx';
import { DossierInquiryListItem } from '../inquiry-item/DossierInquiryListItem.tsx';
import { useDossierSubmissions } from './hooks/useDossierSubmissions.ts';
import { useDossierSubmissionsTable } from './hooks/useDossierSubmissionsTable.ts';
import { useStartableInquiries } from './hooks/useStartableInquiries.ts';

export interface IDossierSubmissionTabProps {
  id: string;
  dossierProcedure: ProcedimentoConTask;
}

/**
 * Pagina che raccoglie i dati delle richieste inviate + le domande presentabili
 */
export function DossierSubmissionTab(props: IDossierSubmissionTabProps) {
  const { id, dossierProcedure } = props;

  const { t } = useTranslation();

  // Nello stato solo l'ordinamento: l'id della pratica è sempre quello corrente
  const [ordering, setOrdering] =
    useState<GetFrontRichiesteParams['ordinamenti']>();

  const submissionsResult = useDossierSubmissions({
    idIstanzaProcedimentoPratica: id,
    ordinamenti: ordering,
  });
  const { columns, onTableChange } = useDossierSubmissionsTable({
    ordering,
    setOrdering,
  });
  const inquiriesResult = useStartableInquiries(dossierProcedure);

  const { submissions } = submissionsResult;
  const { inquiries } = inquiriesResult;

  if (submissionsResult.isLoading || inquiriesResult.isLoading) {
    return <LoaderSpin transparent />;
  }

  if (submissionsResult.error ?? inquiriesResult.error) {
    return (
      <NetworkAlertPage
        noLayout
        error={submissionsResult.error ?? inquiriesResult.error}
        message={t(
          'dossier-page.errore-caricamento-pratica',
          'Si è verificato un errore durante il caricamento della pratica.',
        )}
      />
    );
  }

  return (
    <>
      {inquiries.length > 0 && (
        <div>
          <TitleDivider>
            {t(
              'pratica.dettaglio.domande-presentabili',
              'Domande presentabili',
            )}
          </TitleDivider>

          <Flex vertical>
            {inquiries.map(inquiry => {
              return (
                <DossierInquiryListItem
                  key={inquiry.id}
                  inquiry={inquiry}
                  startDate={inquiry.startDate?.toString()}
                  endDate={inquiry.endDate?.toString()}
                  actions={
                    <StartSecondaryInquiryButton
                      inquiryId={inquiry.id}
                      procedureDossierId={dossierProcedure.id}
                      // Al momento è sempre possibile avviare una domanda secondaria
                      // (quelle scadute non vengono mostrate)
                      // disabled={!canBeStarted}
                      // tooltip={tooltip}
                    />
                  }
                />
              );
            })}
          </Flex>
          <VerticalSpace />
        </div>
      )}

      {submissions.length > 0 && (
        <TitleDivider>
          {t('pratica.dettaglio.richieste-presentate', 'Richieste presentate')}
        </TitleDivider>
      )}
      <TableData
        showSorterTooltip={false}
        size="small"
        dataSource={submissions}
        loading={submissionsResult.isLoading || submissionsResult.isFetching}
        pagination={false}
        columns={columns}
        onChange={onTableChange}
      />
    </>
  );
}
